import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  FileText,
  Settings,
  Target,
  Zap,
} from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import type { DocumentationSection } from '../types/documentation.types'

interface DocumentationNavigationProps {
  sections: DocumentationSection[]
  collapsedSections: Set<string>
  onToggleSection: (sectionId: string) => void
  onItemSelect: (sectionId: string, itemId: string) => void
  selectedSection: string | null
  selectedItem: string | null
}

// biome-ignore lint/suspicious/noExplicitAny: required for dynamic icon component map
const sectionIcons: Record<string, any> = {
  installation: Settings,
  'getting-started': Zap,
  'signal-types': Target,
  advanced: FileText,
}

export function DocumentationNavigation({
  sections,
  collapsedSections,
  onToggleSection,
  onItemSelect,
  selectedSection,
  selectedItem,
}: DocumentationNavigationProps) {
  return (
    <div className='h-full overflow-y-auto'>
      <div className='py-2'>
        {sections.map(section => {
          const isCollapsed = collapsedSections.has(section.id)
          const Icon = sectionIcons[section.id] || BookOpen

          return (
            <div key={section.id} className='mb-1'>
              {/* Section Header */}
              <Button
                variant='ghost'
                className='w-full justify-between px-4 py-3 h-auto font-medium text-left'
                onClick={() => onToggleSection(section.id)}
              >
                <div className='flex items-center gap-3'>
                  <Icon className='h-4 w-4 text-muted-foreground' />
                  <span>{section.title}</span>
                </div>
                {isCollapsed ? (
                  <ChevronRight className='h-4 w-4 text-muted-foreground' />
                ) : (
                  <ChevronDown className='h-4 w-4 text-muted-foreground' />
                )}
              </Button>

              {/* Section Items */}
              {!isCollapsed && (
                <div className='ml-6 border-l border-gray-200'>
                  {section.items.map(item => {
                    const isSelected =
                      selectedSection === section.id && selectedItem === item.id

                    return (
                      <Button
                        key={item.id}
                        variant='ghost'
                        className={`w-full justify-start pl-6 py-2 h-auto text-left text-sm relative ${
                          isSelected
                            ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-600'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        onClick={() => onItemSelect(section.id, item.id)}
                      >
                        {/* Connection line */}
                        <div className='absolute left-0 top-1/2 w-4 h-px bg-gray-200' />

                        <div className='flex-1'>{item.title}</div>
                      </Button>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
